import { defineStore } from "pinia";
import { ref, computed } from "vue";

import {
  joinCommunity as joinCommunityReq,
  leaveCommunity as leaveCommunityReq,
  updateCommunity as updateCommunityReq,
} from "@/api/repositories/user/communities";
import {
  ownProfile,
  updateProfile as updateProfileReq,
} from "@/api/repositories/user";
import {
  updateOneSignal as updateOneSignalReq,
  deleteOneSignal as deleteOneSignalReq,
} from "@/api/repositories/user";

import { useBalanceStore } from "./balance";
import { usePropertiesStore } from "./properties";
import { useProfilesStore } from "./profiles";
import {
  updateSettings as updateSettingsReq,
  resetSettings as resetSettingsReq,
} from "@/api/repositories/user/settings";

import { useNativeCheckPushPermission } from "@/composables/useNativeCheckPushPermission";
import { useNativeGetPushSubscription } from "@/composables/useNativeGetPushSubscription";
import { isFeatureEnabled } from "@/plugins/posthog";
import config from "@/config";
import { shuffleArray } from "@/utils/helpers";

export const useProfileStore = defineStore(
  "profile",
  () => {
    const balanceStore = useBalanceStore();
    const propertiesStore = usePropertiesStore();
    const profilesStore = useProfilesStore();

    const { checkNativePushPermission } = useNativeCheckPushPermission();
    const { getPushSubscription } = useNativeGetPushSubscription();

    const etags = ref({});
    const profile = ref(undefined);
    const gallery = ref({});
    const isLoadingBalance = ref(false);
    const community = computed(() => profile.value?.communities?.[0]);
    const isInCommunity = computed(() => !!community.value);
    const isPerformingLike = ref(false);
    const isPerformingDislike = ref(false);
    const isNativePushSubscribed = ref(false);
    const canAskForNativePush = ref(false);
    const boostingEnabled = ref(true); // Default to true, will be set by PostHog feature flag
    const settingsUpdatedAt = ref(null);

    const fetchProfile = async () => {
      isLoadingBalance.value = true;
      setProfile((await ownProfile()).user);
      await defineSettingsOnBackend();

      // Set boosting enabled flag from PostHog feature flag
      try {
        if (config.env !== "dev") {
          boostingEnabled.value = await isFeatureEnabled("boosting-enabled");
        }
      } catch (error) {
        console.warn("Failed to fetch boosting-enabled feature flag:", error);
        // Default to true if feature flag check fails
        boostingEnabled.value = true;
      }

      isLoadingBalance.value = false;
    };

    const setProfile = (newProfile) => {
      profile.value = newProfile;
      balanceStore.setNewBalance(profile.value.balance);
    };

    /**
     * This function should be only called once on the initialization logic
     * to persist current frontend filters into backend.
     * @returns {Promise<void>}
     */
    const defineSettingsOnBackend = async () => {
      if (profile.value?.settings?.isDefined) {
        return;
      }
      const currentRules = profilesStore.rules;
      const settings = {
        distance: currentRules.distance,
        min_age: currentRules.age.min,
        max_age: currentRules.age.max,
        adjust_age: currentRules.adjustAgeIfNoProfiles,
        adjust_distance: currentRules.adjustDistanceIfNoProfiles,
        only_community: currentRules.onlyCommunityMode,
        looking_for_enum_value_id: currentRules.lookingForEnumValueId,
        adjust_looking_for: currentRules.adjustLookingForIfNoProfiles,
      };
      profile.value.settings = settings;
      await updateSettings();
    };

    const updateSettings = async (settings = {}) => {
      profile.value.settings = (
        await updateSettingsReq({ ...profile.value.settings, ...settings })
      ).settings;
      settingsUpdatedAt.value = Date.now();
    };

    const resetSettings = async () => {
      profile.value.settings = (await resetSettingsReq()).settings;
      settingsUpdatedAt.value = Date.now();
    };

    const refreshSettings = async () => {
      profile.value.settings = (await ownProfile()).user.settings;
      settingsUpdatedAt.value = Date.now();
    };

    const resetProfile = () => {
      profile.value = undefined;
      balanceStore.value = undefined;
      gallery.value = undefined;
      isLoadingBalance.value = undefined;
    };

    const resetEtags = () => {
      etags.value = {};
    };

    const updateProfile = async (data) => {
      const resData = await updateProfileReq(data);
      setProfile(resData.user);
    };

    const joinCommunity = async (data) => {
      await joinCommunityReq(data);
    };

    const leaveCommunity = async (communityId) => {
      await leaveCommunityReq(communityId);
    };

    const updateCommunity = async (communityId, data) => {
      await updateCommunityReq(communityId, data);
    };

    const updateOneSignal = async (subscriptionId) => {
      await updateOneSignalReq(subscriptionId);
    };

    const deleteOneSignal = async () => {
      await deleteOneSignalReq();
    };

    const isPushNotificationSubscribed = computed(() => {
      return profile.value && profile.value.push_subscribed === true;
    });

    const setPushSubscribe = (value) => {
      profile.value.push_subscribed = value;
    };

    const isEtagExists = (etag) => {
      for (const url in etags.value) {
        if (etags.value[url] === etag) {
          return true;
        }
      }
      return false;
    };

    const addEtag = (url, etag) => {
      etags.value[url] = etag;
    };

    const removeEtag = (url) => {
      etags.value[url] && delete etags.value[url];
    };

    const cards = {
      prompts: { id: "prompts" },
      bio: { id: "bio" },
      interests: { id: "interests" },
      verification: { id: "verification" },
      chatsUpdate: { id: "chatsUpdate" },
      premiumSubscription: { id: "premium-subscription" },
      dailyStats: { id: "daily-stats" }
    };

    const seenUpdateCardIds = ref([]);
    const seenChatUpdates = ref(false);
    const seenPremiumSubscription = ref(false);
    const seenSwipeTutorial = ref(false);

    const updateCardSeen = (id) => {
      if (!seenUpdateCardIds.value.includes(id)) {
        seenUpdateCardIds.value.push(id);
      }
    };

    const hasSeenCard = (id) => seenUpdateCardIds.value.includes(id);

    const getPendingUpdates = () => {
      const updates = [];

      if (!hasSeenCard(cards.prompts.id)) {
        updates.push(cards.prompts);
      }

      if (
        propertiesStore.answeredBio?.length < 3 &&
        !hasSeenCard(cards.bio.id)
      ) {
        updates.push(cards.bio);
      }

      if (
        propertiesStore.selectedInterests?.length < 3 &&
        !hasSeenCard(cards.interests.id)
      ) {
        updates.push(cards.interests);
      }

      if (
        !hasSeenCard(cards.chatsUpdate.id) &&
        !seenChatUpdates.value
      ) {
        updates.push(cards.chatsUpdate);
      }

      if (
        !hasSeenCard(cards.premiumSubscription.id) &&
        !seenPremiumSubscription.value
      ) {
        updates.push(cards.premiumSubscription);
      }

      if (
        !hasSeenCard(cards.dailyStats.id)
      ) {
        updates.push(cards.dailyStats);
      }
      shuffleArray(updates);

      if (
        (!profile.value?.verification_status ||
          profile.value?.verification_status === "rejected") &&
        !hasSeenCard(cards.verification.id)
      ) {
        updates.unshift(cards.verification);
      }

      if (updates.length === 0) {
        seenUpdateCardIds.value = [];
      }

      return updates;
    };

    const checkNativePush = async () => {
      try {
        if (!window.SababuuNativeBridge) return;

        const { pushAccessGranted, canRequestPushAccess } =
          await checkNativePushPermission();

        canAskForNativePush.value = canRequestPushAccess;

        if (!pushAccessGranted) {
          await deleteOneSignal();
          return;
        }

        const subscription = await getPushSubscription();
        await updateOneSignal(subscription);
        isNativePushSubscribed.value = true;
      } catch (ex) {
        console.error(ex);
        isNativePushSubscribed.value = false;
      }
    };

    return {
      profile,
      gallery,
      isPushNotificationSubscribed,
      isLoadingBalance,
      etags,
      isInCommunity,
      community,
      fetchProfile,
      resetProfile,
      setProfile,
      updateProfile,
      joinCommunity,
      leaveCommunity,
      updateCommunity,
      updateOneSignal,
      deleteOneSignal,
      setPushSubscribe,
      isEtagExists,
      addEtag,
      removeEtag,
      resetEtags,
      isPerformingLike,
      isPerformingDislike,
      getPendingUpdates,
      updateCardSeen,
      defineSettingsOnBackend,
      resetSettings,
      updateSettings,
      refreshSettings,
      checkNativePush,
      canAskForNativePush,
      isNativePushSubscribed,
      boostingEnabled,
      settingsUpdatedAt,
      seenUpdateCardIds,
      seenChatUpdates,
      seenPremiumSubscription,
      seenSwipeTutorial
    };
  },
  {
    persist: {
      paths: [
        "profile",
        "gallery",
        "etags",
        "isNativePushSubscribed",
        "canAskForNativePush",
        "seenChatUpdates",
        "seenPremiumSubscription",
        "seenSwipeTutorial",
      ],
    },
  }
);
